import { Link } from "react-router";
import { useTranslation } from "react-i18next";

import { ROUTES } from "@/shared/config";
import { Icon } from "@/shared/ui";

/**
 * Шапка экрана: заголовок с подписью и кнопка уведомлений.
 *
 * Собственной верхней панели с логотипом, крестиком и «…» у приложения нет и
 * не будет: ровно над этим местом Telegram рисует свою — с тем же крестиком и
 * тем же меню. Две одинаковые шапки одна над другой читаются как дефект, а не
 * как брендирование, поэтому из макета взята только эта, нижняя часть.
 *
 * Квадрат «назад» опционален. На большинстве экранов его роль играет нативная
 * BackButton Telegram (`useTelegramBackButton`), но на подэкранах, куда пришли
 * из меню — рейтинг, кошелёк, — макет рисует его явно.
 */
interface TopBarProps {
  title: string;
  subtitle: string;
  /** Показать квадрат «назад» слева от заголовка. */
  back?: { to: string; label: string };
}

export function TopBar({ title, subtitle, back }: TopBarProps) {
  const { t } = useTranslation();

  return (
    <header className="flex items-start gap-2">
      {back ? (
        <Link
          to={back.to}
          aria-label={back.label}
          className={[
            "press mt-0.5 flex size-[var(--control-back)] shrink-0 items-center justify-center",
            "rounded-[9px] border border-border-subtle bg-background-raised/70 text-text-secondary",
          ].join(" ")}
        >
          <Icon name="back" size={18} />
        </Link>
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <h1 className="truncate text-h1 font-bold tracking-tight text-text-bright">{title}</h1>
        <p className="truncate text-small font-normal text-text-secondary">{subtitle}</p>
      </div>

      <Link
        to={ROUTES.notifications}
        aria-label={t("home.notifications")}
        className={[
          "press flex size-[var(--control-bell)] shrink-0 items-center justify-center rounded-md",
          "border border-border-subtle bg-[image:var(--surface-control)] text-status-profit",
          // Зелёный подсвет внутрь — из макета (`.bell-btn`): кнопка должна
          // читаться как «здесь что-то происходит», не будучи при этом CTA.
          "shadow-[inset_0_0_18px_color-mix(in_srgb,var(--color-status-profit)_4%,transparent)]",
        ].join(" ")}
      >
        <Icon
          name="bell"
          size={19}
          className="drop-shadow-[0_0_6px_color-mix(in_srgb,var(--color-status-profit)_25%,transparent)]"
        />
      </Link>
    </header>
  );
}
